import { Component, Input, OnChanges, ChangeDetectionStrategy, signal, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilenameFromUrlPipe } from 'src/shared/pipes/filename-from-url.pipe';

@Component({
  selector: 'csv-preview',
  standalone: true,
  imports: [CommonModule, FilenameFromUrlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./csv-preview.component.scss'],
  template: `
    @if (loading()) {
      <div class="csv-preview-container">
        <div class="csv-loading">Loading preview...</div>
      </div>
    } @else if (error()) {
      <div class="csv-preview-container">
        <div class="csv-fallback">
          <img src="assets/svg/document-text-outline.svg" class="csv-icon" />
          <span class="csv-filename">{{ src | filenameFromUrl }}</span>
          @if (downloadAction) {
            <button class="csv-download-btn" (click)="downloadAction(src)">
              <img src="assets/svg/download-outline.svg" class="csv-download-icon" />
            </button>
          }
        </div>
      </div>
    } @else {
      <div class="csv-preview-container">
        <div class="csv-table-wrapper">
          <table>
            @if (headers().length > 0) {
              <thead>
                <tr>
                  @for (header of headers(); track header) {
                    <th>{{ header }}</th>
                  }
                </tr>
              </thead>
            }
            <tbody>
              @for (row of rows(); track $index) {
                <tr>
                  @for (cell of row; track $index) {
                    <td>{{ cell }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
        @if (totalRows() > maxRows) {
          <div class="csv-truncated">
            Showing {{ maxRows }} of {{ totalRows() }} rows
          </div>
        }
        <div class="csv-footer">
          <span class="csv-filename">{{ src | filenameFromUrl }}</span>
          @if (downloadAction) {
            <button class="csv-download-btn" (click)="downloadAction(src)">
              <img src="assets/svg/download-outline.svg" class="csv-download-icon" />
            </button>
          }
        </div>
      </div>
    }
  `
})
export class CsvPreviewComponent implements OnChanges {
  @Input() src = '';
  @Input() downloadAction?: (url: string) => void;
  readonly maxRows = 10;

  private zone = inject(NgZone);
  private lastFetchedSrc = '';

  readonly loading = signal(true);
  readonly error = signal(false);
  readonly headers = signal<string[]>([]);
  readonly rows = signal<string[][]>([]);
  readonly totalRows = signal(0);

  ngOnChanges(): void {
    if (this.src && this.src !== this.lastFetchedSrc) {
      this.lastFetchedSrc = this.src;
      this.fetchCsv();
    }
  }

  private async fetchCsv(): Promise<void> {
    this.loading.set(true);
    this.error.set(false);
    try {
      const response = await fetch(this.src);
      if (!response.ok) {throw new Error(`HTTP ${response.status}`);}
      const text = await response.text();
      this.zone.run(() => {
        this.parseCsv(text);
        this.loading.set(false);
      });
    } catch {
      this.zone.run(() => {
        this.error.set(true);
        this.loading.set(false);
      });
    }
  }

  private parseCsv(text: string): void {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) {
      this.error.set(true);
      return;
    }
    const parseRow = (line: string): string[] => {
      const cells: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          cells.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      cells.push(current.trim());
      return cells;
    };
    this.headers.set(parseRow(lines[0]));
    const dataLines = lines.slice(1);
    this.totalRows.set(dataLines.length);
    this.rows.set(dataLines.slice(0, this.maxRows).map(parseRow));
  }
}
